/// <amd-module name="Test/TestPlansHubRefresh/TestSuiteImportPanel/Redux/TestSuiteImportPanelSaga" />
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import { ITestHubDataService } from "Test/TestPlansHubRefresh/Service/TestHubDataService";
import { TestHubRefreshConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { SuitesActions } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesActions";
import { CloneTestSuiteOperationInformation, TestPlan, TestSuite } from "TestPlanCommon/Contracts/TestPlan";
import { TestArtifactWithContinuationToken } from "TestPlanCommon/Contracts/TestPlanExtended";
import { TeamProjectReference } from "Tfs/Platform/Contracts/Core";
import { IVssPageContext } from "VSS/Platform/Context";
import { TestSuiteImportPanelActions, TestSuiteImportPanelActionsTypes } from "./TestSuiteImportPanelActions";
import { errorHandledSaga } from "Test/TestPlansHubRefresh/TestHub/Util";
import { isAdvancedLicenseEnabled } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshSelector";
import { TelemetryConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { TelemetryActions } from "Test/TestPlansHubRefresh/TestHub/Redux/TelemetryActions";

export function* TestSuiteImportPanelSaga(): SagaIterator {
    yield effects.takeLatest(TestSuiteImportPanelActionsTypes.getProjectsFromServer, _getProjectsFromServer);
    yield effects.takeLatest(TestSuiteImportPanelActionsTypes.getTestPlans, _getTestPlans);
    yield effects.takeLatest(TestSuiteImportPanelActionsTypes.getTestSuitesForPlan, _getTestSuitesForPlan);
    yield effects.takeLatest(TestSuiteImportPanelActionsTypes.copyTestSuite, _copyTestSuite);
}

export function* getProjectsFromServer(
    action: ActionsOfType<TestSuiteImportPanelActions, TestSuiteImportPanelActionsTypes.getProjectsFromServer>
): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;

    const projectList: TeamProjectReference[] = (yield effects.call([dataService, dataService.getProjects])) as any;

    yield effects.put(TestSuiteImportPanelActions.projectsFetchSucceeded(projectList));
}

export function* getTestPlans(action: ActionsOfType<TestSuiteImportPanelActions, TestSuiteImportPanelActionsTypes.getTestPlans>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
    const project: string = action.payload.projectName;

    let continuationToken = action.payload.continuationToken;

    const planListWithToken: TestArtifactWithContinuationToken<TestPlan> = (yield effects.call(
        [dataService, dataService.getTestPlans],
        project,
        undefined,
        continuationToken
    )) as any;

    yield effects.put(TestSuiteImportPanelActions.testPlansFetchSucceeded(project, planListWithToken.results));

    const pageSize: number = 200; // WIT constant, will move to a constants file in a subsequent commit.
    if (planListWithToken.continuationToken) {
        const tokenSplits: string[] = planListWithToken.continuationToken.split(";");
        if (tokenSplits && tokenSplits.length == 2) {
            tokenSplits[1] = pageSize.toString();
            planListWithToken.continuationToken = tokenSplits.join(";");
        }
        yield effects.put(TestSuiteImportPanelActions.getTestPlans(project, planListWithToken.continuationToken));
    }
}

export function* getTestSuitesForPlan(
    action: ActionsOfType<TestSuiteImportPanelActions, TestSuiteImportPanelActionsTypes.getTestSuitesForPlan>
): SagaIterator {
    try {
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;

        const suitesList: TestSuite[] = (yield effects.call(
            [dataService, dataService.getTestSuitesForPlan],
            action.payload.planId,
            undefined,
            undefined,
            true,
            action.payload.project
        )) as any;

        yield effects.put(TestSuiteImportPanelActions.getTestSuitesForPlanSucceeded(suitesList));
    } catch (error) {
        const telemetryPayload = {
            planId: action.payload.planId,
            errorMessage: error.message
        }
        yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.IMPORT_TESTPLAN_SELECTION_ERROR, telemetryPayload));
        yield effects.put(TestSuiteImportPanelActions.disableTestSuiteEditableDropdownSelection(true));
        yield effects.put(TestSuiteImportPanelActions.testSuiteImportPanelError(error.message));
    }
}

export function* copyTestSuite(action: ActionsOfType<TestSuiteImportPanelActions, TestSuiteImportPanelActionsTypes.copyTestSuite>): SagaIterator {
    try {
        yield effects.put(TestSuiteImportPanelActions.copyTestSuiteInProgress());
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

        const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
        let cloneTestSuiteOperationInformation: CloneTestSuiteOperationInformation = (yield effects.call(
            [dataService, dataService.cloneTestSuite],
            action.payload.sourceTestSuiteId,
            action.payload.sourceProject,
            action.payload.destinationTestSuiteId,
            action.payload.destinationAreaPath,
            action.payload.destinationIterationPath,
            false
        )) as any;
        //let suiteId: number = cloneTestSuiteOperationInformation.clonedTestSuite ? cloneTestSuiteOperationInformation.clonedTestSuite.id : -1;
        let opId: number = cloneTestSuiteOperationInformation.cloneOperationResponse
            ? cloneTestSuiteOperationInformation.cloneOperationResponse.opId
            : -1;
        if (opId > 0) {
            yield effects.put(SuitesActions.getSuiteCloneOpState(action.payload.destinationPlanId, opId, true, action.payload.sourceProject));
            const telemetryPayload = {
                sourceTestSuiteId: action.payload.sourceTestSuiteId,
                destinationTestSuiteId: action.payload.destinationTestSuiteId,
                destinationPlanId: action.payload.destinationPlanId,
                isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
            };
            yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.IMPORT_TEST_SUITE, telemetryPayload));
            yield effects.put(TestSuiteImportPanelActions.copyTestSuiteCompleted(opId));
        } else {
            yield effects.put(SuitesActions.showToastNotifications("Import suite operation failed.", TestHubRefreshConstants.TOAST_MESSAGE_DURATION));
            yield effects.put(TestSuiteImportPanelActions.copyTestSuiteCompleted(0));
        }
    } catch (e) {
        console.log(e);
        yield effects.put(SuitesActions.showToastNotifications(e.message, TestHubRefreshConstants.TOAST_MESSAGE_DURATION));
        yield effects.put(TestSuiteImportPanelActions.copyTestSuiteCompleted(0));
    }
}

const _getProjectsFromServer = errorHandledSaga(function* (
    action: ActionsOfType<TestSuiteImportPanelActions, TestSuiteImportPanelActionsTypes.getProjectsFromServer>
): SagaIterator {
    yield effects.call(getProjectsFromServer, action);
});

const _getTestPlans = errorHandledSaga(function* (
    action: ActionsOfType<TestSuiteImportPanelActions, TestSuiteImportPanelActionsTypes.getTestPlans>
): SagaIterator {
    yield effects.call(getTestPlans, action);
});

const _getTestSuitesForPlan = errorHandledSaga(function* (
    action: ActionsOfType<TestSuiteImportPanelActions, TestSuiteImportPanelActionsTypes.getTestSuitesForPlan>
): SagaIterator {
    yield effects.call(getTestSuitesForPlan, action);
});

const _copyTestSuite = errorHandledSaga(function* (
    action: ActionsOfType<TestSuiteImportPanelActions, TestSuiteImportPanelActionsTypes.copyTestSuite>
): SagaIterator {
    yield effects.call(copyTestSuite, action);
});
